import { css } from '@styled-system/css';
import { Clock, Euro, Package, Percent, PiggyBank, Rocket, SlidersHorizontal, TrendingUp } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { TrendFilterValues } from '@/features/trend/types';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Popover } from '@/shared/components/ui/Popover';

// ============================================================================
// Filtres locaux du tableau Trend — mêmes seuils de MÉTRIQUES que le popover
// des niches d'une analyse (NicheFilterPopover) : ils masquent des lignes,
// ils ne changent ni le classement serveur ni les rangs affichés.
// Spécificités Trend : CA/mois et croissance (négatif permis) remplacent le
// score, pas de filtre de type de niche.
// ============================================================================

const content = css({
  display: 'flex',
  flexDirection: 'column',
  width: '320px',
});

const fieldRow = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '2.5',
  paddingY: '4',
  // Séparation légère entre les champs pour guider la lecture
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.subtle',
  _first: { paddingTop: '0' },
  _last: { borderBottomWidth: '0', paddingBottom: '0' },
});

const fieldLabel = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2.5',
  fontSize: 'caption',
  color: 'text.secondary',
  fontWeight: 'medium',
});

const fieldIcon = css({
  display: 'inline-flex',
  alignItems: 'center',
  color: 'text.tertiary',
});

const rangeInputs = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
});

const rangeSeparator = css({
  color: 'text.tertiary',
  fontSize: 'caption',
});

const inputStyle = css({ width: '120px' });

const unitStyle = css({
  fontSize: 'caption',
  color: 'text.tertiary',
});

const footerRow = css({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: '4',
});

const clearButton = css({
  background: 'transparent',
  border: 'none',
  fontSize: 'caption',
  fontFamily: 'sans',
  color: 'text.tertiary',
  cursor: 'pointer',
  padding: '1',
  _hover: { color: 'text.primary', textDecoration: 'underline' },
});

const parseNumber = (raw: string, allowNegative = false): number | undefined => {
  if (raw.trim() === '') return undefined;
  const value = Number(raw);
  if (!Number.isFinite(value)) return undefined;
  return allowNegative || value >= 0 ? value : undefined;
};

interface RangeFieldProps {
  icon: ReactNode;
  label: string;
  /** Unité affichée dans les champs (€, %, h…). */
  unit?: string;
  /** true : bornes négatives permises (croissance). */
  allowNegative?: boolean;
  min: number | undefined;
  max: number | undefined;
  minPlaceholder: string;
  maxPlaceholder: string;
  onChange: (min: number | undefined, max: number | undefined) => void;
}

const RangeField: FC<RangeFieldProps> = ({
  icon,
  label,
  unit,
  allowNegative = false,
  min,
  max,
  minPlaceholder,
  maxPlaceholder,
  onChange,
}) => (
  <div className={fieldRow}>
    <span className={fieldLabel}>
      <span className={fieldIcon}>{icon}</span>
      {label}
    </span>
    <div className={rangeInputs}>
      <Input
        type="number"
        inputSize="sm"
        min={allowNegative ? undefined : 0}
        value={min ?? ''}
        onChange={(event) => onChange(parseNumber(event.target.value, allowNegative), max)}
        placeholder={minPlaceholder}
        trailing={unit ? <span className={unitStyle}>{unit}</span> : undefined}
        className={inputStyle}
      />
      <span className={rangeSeparator}>—</span>
      <Input
        type="number"
        inputSize="sm"
        min={allowNegative ? undefined : 0}
        value={max ?? ''}
        onChange={(event) => onChange(min, parseNumber(event.target.value, allowNegative))}
        placeholder={maxPlaceholder}
        trailing={unit ? <span className={unitStyle}>{unit}</span> : undefined}
        className={inputStyle}
      />
    </div>
  </div>
);

export interface TrendFilterPopoverProps {
  filters: TrendFilterValues;
  onFilterChange: (filters: TrendFilterValues) => void;
  /** Libellé de la croissance — dépend de la liste (hebdo / tendance). */
  growthLabel: string;
}

export const TrendFilterPopover: FC<TrendFilterPopoverProps> = ({ filters, onFilterChange, growthLabel }) => {
  const { t } = useTranslation();

  const hasFilters = Object.values(filters).some((value) => value !== undefined);

  const iconProps = { size: 13, strokeWidth: 1.75 } as const;

  return (
    <Popover
      placement="bottom-start"
      trigger={
        <Button variant="secondary" size="md" icon={<SlidersHorizontal size={13} strokeWidth={1.75} />}>
          {t('niches.filters')}
        </Button>
      }
    >
      <div className={content}>
        <RangeField
          icon={<PiggyBank {...iconProps} />}
          label={t('trend.caPerMonth')}
          unit="€"
          min={filters.caPerMonthMin}
          max={filters.caPerMonthMax}
          minPlaceholder={t('segment.min')}
          maxPlaceholder={t('segment.max')}
          onChange={(caPerMonthMin, caPerMonthMax) => onFilterChange({ ...filters, caPerMonthMin, caPerMonthMax })}
        />

        <RangeField
          icon={<Percent {...iconProps} />}
          label={t('niches.colSoldPct')}
          unit="%"
          min={filters.soldPctMin}
          max={filters.soldPctMax}
          minPlaceholder={t('segment.min')}
          maxPlaceholder={t('segment.max')}
          onChange={(soldPctMin, soldPctMax) => onFilterChange({ ...filters, soldPctMin, soldPctMax })}
        />

        <RangeField
          icon={<TrendingUp {...iconProps} />}
          label={t('niches.colSoldPerDay')}
          min={filters.salesPerDayMin}
          max={filters.salesPerDayMax}
          minPlaceholder={t('segment.min')}
          maxPlaceholder={t('segment.max')}
          onChange={(salesPerDayMin, salesPerDayMax) => onFilterChange({ ...filters, salesPerDayMin, salesPerDayMax })}
        />

        <RangeField
          icon={<Euro {...iconProps} />}
          label={t('niches.colAvgPrice')}
          unit="€"
          min={filters.avgPriceMin}
          max={filters.avgPriceMax}
          minPlaceholder={t('segment.min')}
          maxPlaceholder={t('segment.max')}
          onChange={(avgPriceMin, avgPriceMax) => onFilterChange({ ...filters, avgPriceMin, avgPriceMax })}
        />

        <RangeField
          icon={<Clock {...iconProps} />}
          label={t('niches.colTimeToSell')}
          unit="h"
          min={filters.timeToSellMin}
          max={filters.timeToSellMax}
          minPlaceholder={t('segment.min')}
          maxPlaceholder={t('segment.max')}
          onChange={(timeToSellMin, timeToSellMax) => onFilterChange({ ...filters, timeToSellMin, timeToSellMax })}
        />

        <RangeField
          icon={<Package {...iconProps} />}
          label={t('trend.productCount')}
          min={filters.productCountMin}
          max={filters.productCountMax}
          minPlaceholder={t('segment.min')}
          maxPlaceholder={t('segment.max')}
          onChange={(productCountMin, productCountMax) =>
            onFilterChange({ ...filters, productCountMin, productCountMax })
          }
        />

        <RangeField
          icon={<Rocket {...iconProps} />}
          label={growthLabel}
          unit="%"
          allowNegative
          min={filters.growthMin}
          max={filters.growthMax}
          minPlaceholder={t('segment.min')}
          maxPlaceholder={t('segment.max')}
          onChange={(growthMin, growthMax) => onFilterChange({ ...filters, growthMin, growthMax })}
        />

        {hasFilters && (
          <div className={footerRow}>
            <button type="button" className={clearButton} onClick={() => onFilterChange({})}>
              {t('niches.clearAll')}
            </button>
          </div>
        )}
      </div>
    </Popover>
  );
};
